<template>
  <!-- The LoopLicense sheet. The web is the only checkout (decision 7), so
       the sheet ends in the buy button itself: name your price (decision
       15), then PayPal. A bottom sheet on phones, a centred dialog from
       700 px up. With `pack-target` it buys a whole pack (the app's
       BuySheet for BuyTarget.pack): the same price field and button, the
       pack's terms in place of the sound's preview and facts. With
       `details` it is a sound's details sheet first (SI-16, every license
       state): Star, Share and Close, the terms for free, owned, for sale
       or not for sale, and one call to action (Download, Buy, or Not for
       sale) with Add to session beside it; Buy turns it into the buy
       sheet. -->
  <v-dialog
    v-model="dialogVisible"
    class="cl-buy"
    :transition="sheet ? 'bottom-sheet-transition' : 'dialog-transition'"
  >
    <div class="buy-sheet" :class="{ 'buy-sheet--pack': !!packTarget, 'buy-sheet--details': detailsMode }" :data-test="detailsMode ? 'sound-details' : null">
      <div class="buy-handle" aria-hidden="true"></div>

      <div class="buy-body">
        <header class="buy-head">
          <div class="buy-head-text">
            <h2 class="buy-title">{{ heading }}</h2>
            <div class="buy-sub">
              <span
                class="buy-dot"
                :style="{ background: ownerColor }"
                :title="sellerName"
              ></span>
              <span class="buy-sub-text">{{ subtitle }}</span>
            </div>
            <!-- Decision 15 (the app's BuyTarget.pack note): which sounds
                 come only with the pack. -->
            <p v-if="packNote" class="buy-head-note">{{ packNote }}</p>
          </div>
          <!-- The app's sound sheet: Star and Share beside Close
               (sound_detail.dart). -->
          <div v-if="details && !packTarget" class="buy-head-actions">
            <button
              v-if="signedIn"
              type="button"
              class="buy-icon buy-star"
              :class="{ 'is-on': starred }"
              :aria-label="starred ? 'Unstar' : 'Star'"
              data-test="sound-details-star"
              @click="toggleStarred"
            >
              <svg width="19" height="19" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.9 6.8 19.6l1-5.8-4.3-4.1 5.9-.9L12 3.5Z" :fill="starred ? 'currentColor' : 'none'" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" /></svg>
            </button>
            <button type="button" class="buy-icon buy-share" aria-label="Share" data-test="sound-details-share" @click="shareSound">
              <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 15V4M7.5 8.5 12 4l4.5 4.5M6 12v7a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-7" /></svg>
            </button>
          </div>
          <button type="button" class="buy-close" aria-label="Close" @click="close()">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" /></svg>
          </button>
        </header>

        <div v-if="!packTarget" class="buy-preview" :style="{ '--buy-owner': ownerColor }">
          <button
            type="button"
            class="buy-play"
            :aria-label="isPlaying ? 'Pause preview' : 'Play preview'"
            @click="togglePreview()"
          >
            <v-progress-circular
              v-if="isBuffering"
              indeterminate
              size="18"
              width="2"
              color="var(--cl-on-fill)"
            />
            <svg v-else-if="isPlaying" width="15" height="15" viewBox="0 0 24 24" aria-hidden="true"><path d="M7 5h3.5v14H7zM13.5 5H17v14h-3.5z" fill="currentColor" /></svg>
            <svg v-else width="15" height="15" viewBox="0 0 24 24" aria-hidden="true"><path d="M7 4.5v15L19.5 12 7 4.5Z" fill="currentColor" /></svg>
          </button>
          <WaveMask
            v-if="loopHash"
            class="buy-wave"
            :hash="loopHash"
            :width="540"
            :height="44"
            bars
            :progress="progressPercent"
            seekable
            @seek="seekAudioFraction"
          />
        </div>
        <p v-if="!packTarget && watermarked" class="buy-note">
          Previews have a soft watermark. The license gets you the clean file.
        </p>

        <section class="buy-license" aria-label="License">
          <div class="buy-license-head">
            <span>License</span>
            <span class="buy-license-name">{{ shownLicenseName }}</span>
          </div>
          <div v-for="t in shownTerms" :key="t.text" class="buy-term">
            <svg v-if="t.ok === 'info'" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--cl-label-3)" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3.5a8.5 8.5 0 1 0 0 17 8.5 8.5 0 0 0 0-17ZM12 11v5.5M12 7.5h.01" /></svg>
            <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" :stroke="t.ok ? 'var(--cl-live-text)' : 'var(--cl-label-3)'" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path :d="t.ok ? 'M5 12.5l4.5 4.5L19 7' : 'M6 6l12 12M18 6 6 18'" /></svg>
            <span>{{ t.text }}</span>
          </div>
          <a class="buy-term buy-term--link" :href="termsHref" target="_blank" rel="noopener">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--cl-accent-text)" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 6h9v9M18 6 6 18" /></svg>
            <span>Read the full license</span>
          </a>
        </section>

        <div v-if="facts.length" class="buy-facts">
          <span v-for="f in facts" :key="f">{{ f }}</span>
        </div>

        <!-- The pack this sound is in (the app's _packCard); gone once the
             sound is yours. -->
        <a
          v-if="pack && !claimed && !packTarget && !(detailsMode && soundOwned)"
          class="buy-pack"
          :href="pack.href"
          :aria-label="`Or get the whole pack, ${pack.title}`"
          @click.prevent="openPack()"
        >
          <!-- The pack page's cover (its picture, its art seed, the
               curator's colour), wherever the pack shows. -->
          <ArtTile
            :seed="pack.seed"
            :owner="pack.color"
            pooled
            :picture="pack.picture"
            :size="48"
          />
          <span class="buy-pack-text">
            <span class="buy-pack-title">Or get the whole pack</span>
            <span class="buy-pack-line">{{ pack.line }}</span>
          </span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--cl-label-45)" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 5l7 7-7 7" /></svg>
        </a>
      </div>

      <footer class="buy-foot">
        <!-- Details: the one call to action, Add to session beside it. -->
        <template v-if="detailsMode">
          <div class="buy-cta-row">
            <v-btn
              class="cl-btn cl-btn--hero buy-cta"
              :class="{ 'buy-cta--off': detailsCta.disabled }"
              variant="outlined"
              :disabled="detailsCta.disabled"
              data-test="sound-details-cta"
              @click="onDetailsCta()"
            >
              {{ detailsCta.label }}
            </v-btn>
            <v-btn v-if="canAdd" class="cl-btn buy-add" variant="outlined" data-test="sound-details-add" @click="addSound()">
              Add to session
            </v-btn>
          </div>
          <p v-if="detailsNote" class="buy-foot-note">{{ detailsNote }}</p>
        </template>

        <!-- A $0 minimum the creator chose: licensed without a checkout. -->
        <template v-else-if="claimed">
          <div class="buy-done">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--cl-accent-text)" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7" /></svg>
            <span>{{ claimedLine }}</span>
          </div>
          <v-btn class="cl-btn cl-btn--hero buy-cta" variant="outlined" block @click="openMyAudio()">
            Open my audio
          </v-btn>
        </template>

        <!-- Decision 15: rights reserved with no price is not for sale. -->
        <template v-else-if="notForSale">
          <v-btn class="cl-btn cl-btn--hero buy-cta buy-cta--off" variant="outlined" block disabled>
            Not for sale
          </v-btn>
          <p class="buy-foot-note">Only {{ sellerName }} can put it up for sale.</p>
        </template>

        <template v-else>
          <div class="buy-price" :class="{ 'buy-price--error': !!priceError }">
            <label class="buy-price-label" :for="fieldId">Your price</label>
            <v-text-field
              :id="fieldId"
              v-model.number="amount"
              class="buy-price-input"
              type="number"
              prefix="$"
              :min="floor"
              step="0.01"
              :rules="[(v) => Number(v) >= floor || `The minimum is ${usd(floor)}.`]"
              :error-messages="amountError"
              variant="plain"
              density="compact"
              hide-details
              @update:model-value="onAmountInput"
              @blur="tidyAmount()"
              @keyup.enter="purchase()"
            />
          </div>
          <p
            class="buy-price-note"
            :class="{ 'buy-price-note--error': !!priceError }"
            role="status"
          >
            {{ priceError || priceNote }}
          </p>
          <v-btn
            class="cl-btn cl-btn--hero buy-cta"
            variant="outlined"
            block
            :loading="loading || (signedIn && pricing)"
            :disabled="signedIn && !isValidAmount"
            @click="purchase()"
          >
            <span>{{ ctaLabel }}</span><svg v-if="signedIn" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 6h9v9M18 6 6 18" /></svg>
          </v-btn>
          <p class="buy-foot-note">{{ footNote }}</p>
        </template>
      </footer>
    </div>
  </v-dialog>
</template>

<script>
import { paneRouteMixin } from "@/shell/paneRoute.js";
import cloudloop from "@/mixins/cloudloop";
import audition from "@/audio/audition.js";
import WaveMask from "@/components/WaveMask.vue";
import ArtTile from "@/components/kit/ArtTile.vue";
import { barItem, whereOf } from "@/components/sounds/soundPlay.js";
import { assign, forName, joinOrder } from "@/kit/people.js";
import { sessionTitle } from "@/kit/sessionName.js";
import { MY_PURCHASES } from "@/kit/myAudio.js";
import { packArtSeed, packPicture, packStartUsd } from "@/kit/packs.js";
import { canDownload, soundCta, toSound } from "@/kit/sound.js";
import { packForSound } from "@/services/api/sounds.js";
import { addToSession, canAddToSession } from "@/services/addToSession.js";
import { download, shareLink, toggleStar } from "@/services/soundActions.js";
import {
  keyBpm,
  packLine,
  buyPrice,
  purchaseErrorText,
  saleTerms,
  soundFacts,
  usd,
} from "@/kit/sale.js";

// Where a checkout started, so the cancel page can offer "Try again"
// (PayPal's return URLs are fixed server-side and can't carry it).
export const CHECKOUT_FROM_KEY = "cl.checkoutFrom";
const CHECKOUT_FROM_TTL_MS = 60 * 60 * 1000;

export function readCheckoutFrom() {
  try {
    const p = JSON.parse(window.sessionStorage.getItem(CHECKOUT_FROM_KEY));
    if (
      p &&
      typeof p.path === "string" &&
      p.path.startsWith("/") &&
      Date.now() - p.at < CHECKOUT_FROM_TTL_MS
    ) {
      return p;
    }
  } catch (e) {
    /* missing or unreadable */
  }
  return null;
}

// The success and cancel pages clear it once read, so a later checkout
// that never wrote it (a pack) can't send the buyer back here.
export function clearCheckoutFrom() {
  try {
    window.sessionStorage.removeItem(CHECKOUT_FROM_KEY);
  } catch (e) {
    /* storage unavailable */
  }
}

// Every checkout that can land on /purchase/cancel records where it began.
export function rememberCheckoutFrom(path) {
  try {
    window.sessionStorage.setItem(
      CHECKOUT_FROM_KEY,
      JSON.stringify({ path, at: Date.now() }),
    );
  } catch (e) {
    /* storage unavailable: the cancel page falls back to Home */
  }
}

const PHONE = "(max-width: 699.98px)";
let uid = 0;

export default {
  name: "PurchaseDialog",
  components: { ArtTile, WaveMask },
  mixins: [cloudloop, paneRouteMixin],
  props: {
    show: Boolean,
    loopHash: String,
    loopName: String,
    sellerUsername: String,
    minPrice: {
      type: Number,
      default: 0,
    },
    suggestedPrice: {
      type: Number,
      default: null,
    },
    // Buy a whole pack: { slug, title, curator, curatorName, color, total,
    // minUsd, suggestedUsd, minExplicit, bonusCount }. The sound props are
    // ignored.
    packTarget: {
      type: Object,
      default: null,
    },
    // The sound's details sheet (SI-16; SoundDetailsSheet): its Buy turns
    // the sheet into this buy sheet.
    details: {
      type: Boolean,
      default: false,
    },
    // The kit/sound.js Sound the sheet is about, when the caller has one
    // (license state, ownership, tokens, the packs it is in).
    sound: {
      type: Object,
      default: null,
    },
  },
  // licensed: a $0-minimum sound was claimed without a checkout, so the
  // row can unlock its download.
  emits: ["update:show", "close", "licensed"],
  data() {
    return {
      loading: false,
      amount: null,
      // The buyer typed a price: a late /api/loop answer leaves it alone.
      amountTouched: false,
      amountError: "",
      // /api/loop metadata: key, tempo, file facts and the sale terms.
      meta: null,
      // /api/loop didn't answer: the props' terms stand (never $0, S7).
      metaFailed: false,
      // The seller's display name when the session doesn't carry it.
      sellerDisplay: null,
      sellerDisplayFor: null,
      // "Or get the whole pack": the published pack this sound is in.
      pack: null,
      packFor: null,
      claimed: false,
      sheet: false,
      fieldId: `buy-price-${++uid}`,
      // Details first (the details sheet), or the buy sheet.
      mode: this.details ? "details" : "buy",
      // The preview this sheet started (it stops when the sheet closes).
      startedHere: false,
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.show;
      },
      set(value) {
        this.$emit("update:show", value);
      },
    },
    me() {
      return this.$store.state.loggedInUser?.username || null;
    },
    signedIn() {
      return !!this.me;
    },
    // The open session, when this sound is in it.
    session() {
      const s = this.$store.state.selectedSession;
      const inIt = s && (s.library || []).some((l) => l && l.hash === this.loopHash);
      return inIt ? s : null;
    },
    heading() {
      return this.packTarget ? this.packTarget.title : this.loopName;
    },
    // "1 bonus sound comes only with the pack." (buy_sheet.dart
    // BuyTarget.pack).
    packNote() {
      const n = Number(this.packTarget?.bonusCount) || 0;
      if (n <= 0) return "";
      return `${n} bonus ${n === 1 ? "sound comes" : "sounds come"} only with the pack.`;
    },
    claimedLine() {
      return this.packTarget
        ? "It's yours. Every sound is in My audio, Purchased."
        : "It's yours. The full-quality WAV is in My audio, Purchased.";
    },
    // In a session, the session's colours (you are violet, the others by
    // join order); anywhere else, the colour for the name (decision 39).
    ownerColor() {
      if (this.packTarget) return this.packTarget.color || forName(this.packTarget.curator, this.me);
      const s = this.session;
      if (s && this.sellerUsername) {
        const order = joinOrder({
          creator: s.creator,
          users: s.users || [],
          loopCreators: (s.library || []).map((l) => l && l.creator),
        });
        const colors = assign(order, this.me);
        if (colors[this.sellerUsername]) return colors[this.sellerUsername];
      }
      return forName(this.sellerUsername, this.me);
    },
    // Decision 20: the display name ("Priya S."), else the username.
    sellerName() {
      if (this.packTarget) return this.packTarget.curatorName || this.packTarget.curator;
      const names = this.session?.member_display_names;
      if (names && names[this.sellerUsername]) return names[this.sellerUsername];
      return this.sellerDisplay || this.sellerUsername;
    },
    subtitle() {
      if (this.packTarget) {
        const n = Number(this.packTarget.total) || 0;
        return [this.sellerName, n ? `${n} ${n === 1 ? "sound" : "sounds"}` : null]
          .filter(Boolean)
          .join(" · ");
      }
      return [
        this.sellerName,
        this.session ? sessionTitle(this.session) : this.sound ? whereOf(this.sound) : null,
        keyBpm(this.meta),
      ]
        .filter(Boolean)
        .join(" · ");
    },
    detailsMode() {
      return this.details && this.mode === "details" && !this.packTarget;
    },
    // The sound as a kit/sound.js Sound: the caller's, else one built from
    // the props, the live terms and the page's media tokens.
    theSound() {
      if (this.sound && this.sound.hash === this.loopHash) return this.sound;
      const g = this.$store.getters;
      const row = {
        ...(this.meta || {}),
        hash: this.loopHash,
        title: this.loopName,
        creator: this.sellerUsername,
        stream_token: g.mediaStreamToken ? g.mediaStreamToken(this.loopHash) : null,
        download_token: g.mediaDownloadToken ? g.mediaDownloadToken(this.loopHash) : null,
      };
      return toSound(row, { me: this.me }) || { hash: this.loopHash, title: this.loopName, creator: this.sellerUsername };
    },
    starred() {
      return this.$store.getters.isLoopStarred(this.loopHash);
    },
    soundOwned() {
      const s = this.theSound;
      return !!s && !!s.isOwned && !s.isFree;
    },
    // The watermark note: the buy sheet always; details only while the
    // clean file isn't yours to take.
    watermarked() {
      return !this.detailsMode || !canDownload(this.theSound);
    },
    shownLicenseName() {
      return this.detailsMode ? this.detailsLicenseName : this.licenseName;
    },
    shownTerms() {
      return this.detailsMode ? this.detailsTerms : this.terms;
    },
    // "CC0", "CC BY (credit the creator)", "CC BY-NC", "Rights reserved" or
    // "Custom terms".
    detailsLicenseName() {
      const s = this.theSound;
      const lt = String((this.meta && this.meta.license_type) || s.licenseType || "").toUpperCase();
      if (s.isFree) {
        if (lt === "CC0") return "CC0";
        if (lt === "CC-BY") return "CC BY (credit the creator)";
        return lt.startsWith("CC-") ? `CC ${lt.slice(3)}` : "Free";
      }
      return s.licenseState === "custom" ? "Custom terms" : "Rights reserved";
    },
    // What the license lets you do, in every state (sound_detail.dart).
    detailsTerms() {
      const s = this.theSound;
      const md = this.meta || {};
      const rows = [];
      if (this.soundOwned) {
        rows.push({ ok: true, text: "You hold a license for this sound" });
      } else if (s.isFree) {
        rows.push({ ok: true, text: "Free to use, download the full-quality WAV" });
        const credit = md.require_attribution != null ? md.require_attribution === true : s.requireAttribution;
        rows.push(credit ? { ok: "info", text: `Credit ${this.sellerName} when you use it` } : { ok: true, text: "No credit needed" });
      } else if (s.forSale) {
        rows.push({ ok: true, text: "Download the full-quality WAV" });
        rows.push({ ok: true, text: "Hear it without the preview watermark" });
        rows.push({ ok: true, text: "Duplicate sessions that use this loop" });
      } else {
        rows.push({ ok: false, text: `${this.sellerName} hasn't put this sound up for sale` });
        rows.push({ ok: false, text: "No download; previews only" });
      }
      const commercial = md.allow_commercial != null ? md.allow_commercial !== false : s.allowCommercial !== false;
      if (!commercial) rows.push({ ok: false, text: "No commercial use" });
      return rows;
    },
    // Download, "Buy · $2.99", "Get it free", or the disabled "Not for sale".
    detailsCta() {
      return soundCta(this.theSound);
    },
    canAdd() {
      return canAddToSession(this.theSound);
    },
    detailsNote() {
      const c = this.detailsCta;
      if (c.kind === "buy") return this.footNote;
      if (c.kind === "none") return `Only ${this.sellerName} can put it up for sale.`;
      return "";
    },
    // The preview plays through the one engine (SI-4).
    isPlaying() {
      return audition.isPlaying(this.loopHash);
    },
    isBuffering() {
      return audition.isBuffering(this.loopHash);
    },
    progressPercent() {
      return audition.progressOf(this.loopHash);
    },
    // A pack's terms as the server prices it (purchase_pack): at least the
    // minimum, which is a cent unless the creator chose $0, defaulting to
    // the suggested price.
    sale() {
      const t = this.packTarget;
      if (t) {
        const min = Math.max(Number(t.minUsd) || 0, 0);
        const checkoutMin = t.minExplicit && min === 0 ? 0 : Math.max(min, 0.01);
        const sug = t.suggestedUsd == null ? null : Number(t.suggestedUsd);
        return {
          state: "for_sale",
          min,
          checkoutMin,
          suggested: sug != null && sug > 0 ? Math.max(sug, checkoutMin) : null,
        };
      }
      return this.meta ? saleTerms(this.meta) : null;
    },
    notForSale() {
      return this.sale?.state === "not_for_sale";
    },
    licenseName() {
      return this.sale?.state === "custom" ? "Custom terms" : "Rights reserved";
    },
    terms() {
      if (this.packTarget) {
        return [
          { ok: true, text: "Download every sound as a full-quality WAV" },
          { ok: true, text: "The Ableton Live set, ready to open" },
          { ok: true, text: "Hear them without the preview watermark" },
        ];
      }
      if (this.notForSale) {
        return [
          { ok: false, text: `${this.sellerName} hasn't put this sound up for sale` },
          { ok: false, text: "No download; previews only" },
        ];
      }
      const rows = [
        { ok: true, text: "Download the full-quality WAV" },
        { ok: true, text: "Hear it without the preview watermark" },
        { ok: true, text: "Duplicate sessions that use this loop" },
      ];
      if (this.meta && this.meta.allow_commercial === false) {
        rows.push({ ok: false, text: "No commercial use" });
      }
      return rows;
    },
    facts() {
      return this.packTarget ? [] : soundFacts(this.meta);
    },
    termsHref() {
      return this.$router.resolve("/terms").href;
    },
    // The live terms: a pack's, or the sound's /api/loop answer once it
    // has landed for this sound; null until then.
    liveSale() {
      if (this.packTarget) return this.sale;
      return this.meta && this.meta.hash === this.loopHash ? this.sale : null;
    },
    // The price line and button (kit/sale.js buyPrice): the caller's
    // checkout minimum, or the server's when that is higher; until the
    // live terms land, "Checking the price…" and never free (S7).
    price() {
      return buyPrice({
        minPrice: this.minPrice,
        live: this.liveSale,
        failed: this.metaFailed,
        amount: this.amountValue,
        signedIn: this.signedIn,
      });
    },
    // The least the field takes.
    floor() {
      return this.price.floor;
    },
    // The field holds what was typed ("5.00" until edited); this is the
    // number, or null when it isn't one.
    amountValue() {
      if (this.amount === null || this.amount === "") return null;
      const v = Number(this.amount);
      return Number.isFinite(v) ? v : null;
    },
    isValidAmount() {
      return this.price.valid;
    },
    pricing() {
      return this.price.pricing;
    },
    priceNote() {
      return this.price.note;
    },
    priceError() {
      if (this.amountError) return this.amountError;
      if (this.amountValue === null) return "Enter an amount.";
      if (this.amountValue < this.floor) return `The minimum is ${usd(this.floor)}.`;
      return "";
    },
    ctaLabel() {
      return this.price.cta;
    },
    footNote() {
      return this.signedIn
        ? "Pay with PayPal. It shows up in My audio, Purchased, as soon as payment clears."
        : "Buying needs an account. Log in and you come straight back here.";
    },
  },
  watch: {
    show(newVal) {
      if (newVal) this.open();
      else this.stopPreview();
    },
  },
  mounted() {
    this.mql = window.matchMedia ? window.matchMedia(PHONE) : null;
    this.sheet = !!(this.mql && this.mql.matches);
    this.onMedia = (e) => (this.sheet = e.matches);
    if (this.mql && this.mql.addEventListener) {
      this.mql.addEventListener("change", this.onMedia);
    }
    if (this.show) this.open();
    // Back from PayPal through the browser's page cache: the sheet is live
    // again, not stuck on its spinner.
    this.onPageShow = (e) => {
      if (e.persisted) this.loading = false;
    };
    window.addEventListener("pageshow", this.onPageShow);
  },
  beforeUnmount() {
    // Another sheet replacing this one (SoundSheetsHost swaps them) never
    // closes it first: its preview stops here too (L4).
    this.stopPreview();
    if (this.mql && this.mql.removeEventListener) {
      this.mql.removeEventListener("change", this.onMedia);
    }
    window.removeEventListener("pageshow", this.onPageShow);
  },
  methods: {
    usd,
    open() {
      // The suggested price, else the minimum (decision 15); the live terms
      // replace the props' once /api/loop answers.
      this.amountTouched = false;
      this.amount = this.defaultAmount();
      this.amountError = "";
      this.claimed = false;
      this.mode = this.details ? "details" : "buy";
      if (this.packTarget) return;
      this.loadMeta();
      this.loadSellerName();
      this.loadPack();
    },
    close() {
      this.dialogVisible = false;
      this.$emit("close");
    },
    defaultAmount() {
      const live =
        this.packTarget || (this.meta && this.meta.hash === this.loopHash) ? this.sale : null;
      if (live && live.state === "for_sale") {
        return Number(live.suggested ?? live.checkoutMin ?? 0).toFixed(2);
      }
      return Number(this.suggestedPrice || this.minPrice || 1).toFixed(2);
    },
    onAmountInput() {
      this.amountError = "";
      this.amountTouched = true;
    },
    async loadMeta() {
      const hash = this.loopHash;
      if (!hash || (this.meta && this.meta.hash === hash)) return;
      this.meta = null;
      this.metaFailed = false;
      try {
        const base = process.env.VUE_APP_API_URL || "";
        const res = await fetch(`${base}/api/loop?id=${encodeURIComponent(hash)}`);
        const body = await res.json();
        const md = body?.data?.results;
        // Extras only: without them the sheet runs on its props.
        if (md && this.loopHash === hash) {
          this.meta = { ...md, hash };
          if (!this.amountTouched) this.amount = this.defaultAmount();
        } else if (this.loopHash === hash) {
          this.metaFailed = true;
        }
      } catch (e) {
        /* offline or unknown loop: no facts, terms from the props */
        if (this.loopHash === hash) this.metaFailed = true;
      }
    },
    // Only when the open session doesn't already name the seller.
    async loadSellerName() {
      const seller = this.sellerUsername;
      const names = this.session?.member_display_names;
      if (!seller || (names && seller in names)) return;
      if (this.sellerDisplayFor === seller) return;
      this.sellerDisplay = null;
      try {
        const doc = (await this.fetchUser(encodeURIComponent(seller)))?.data?.results;
        if (this.sellerUsername === seller) {
          this.sellerDisplay = (doc && doc.display_name) || null;
          this.sellerDisplayFor = seller;
        }
      } catch (e) {
        /* the username stands in */
      }
    },
    // A pack holds its curator's own sounds (decision 12), so the seller's
    // published packs are the only places this sound can be.
    async loadPack() {
      const hash = this.loopHash;
      const seller = this.sellerUsername;
      if (!hash || !seller || this.packFor === hash) return;
      this.pack = null;
      this.packFor = hash;
      try {
        // The packs the row names (B6), else the seller's (as before).
        const packs = this.sound && this.sound.hash === hash ? this.sound.packs : null;
        const hit = await packForSound({ hash, seller, packs });
        if (!hit || this.loopHash !== hash) return;
        this.pack = {
          slug: hit.slug,
          title: hit.title || hit.slug,
          href: this.$router.resolve(`/pack/${encodeURIComponent(hit.slug)}`).href,
          // The pack page's art: never the seller's in-session colour.
          seed: packArtSeed(hit),
          picture: packPicture(hit),
          color: forName(hit.curator_username || seller, this.me),
          // The price the pack's Buy button names (where its sheet starts).
          line: packLine({
            title: hit.title || hit.slug,
            total: hit.summary?.total ?? (hit.loops || []).length,
            price: hit.summary
              ? packStartUsd(hit.summary)
              : packStartUsd({
                  pack_price_usd: hit.pack_min_usd,
                  pack_suggested_usd: hit.pack_suggested_usd,
                }),
          }),
        };
      } catch (e) {
        /* no pack row: the sheet sells the sound alone */
        this.packFor = null;
      }
    },
    openPack() {
      if (!this.pack) return;
      const slug = this.pack.slug;
      this.close();
      this.$router.push(`/pack/${encodeURIComponent(slug)}`);
    },
    tidyAmount() {
      if (this.amountValue !== null) this.amount = this.amountValue.toFixed(2);
    },
    togglePreview() {
      if (audition.isCurrent(this.loopHash)) {
        audition.toggle();
        return;
      }
      const sound = { ...this.theSound, duration: this.theSound.duration || Number(this.meta?.duration) || null };
      this.startedHere = true;
      audition.playSound(sound, { item: barItem(sound, { me: this.me, color: this.ownerColor }) });
    },
    seekAudioFraction(frac) {
      if (audition.isCurrent(this.loopHash)) audition.seek(frac);
      else this.togglePreview();
    },
    // A preview this sheet started stops with it, as before.
    stopPreview() {
      if (this.startedHere && audition.isCurrent(this.loopHash)) audition.stop();
      this.startedHere = false;
    },
    toggleStarred() {
      toggleStar(this.theSound);
    },
    shareSound() {
      shareLink(this.theSound, this.me);
    },
    // Details' call to action: take it, or turn into the buy sheet.
    onDetailsCta() {
      const c = this.detailsCta;
      if (c.disabled) return;
      if (c.kind === "download") {
        download(this.theSound);
        return;
      }
      if (!this.signedIn) {
        this.goToLogin();
        return;
      }
      this.amount = this.defaultAmount();
      this.mode = "buy";
    },
    addSound() {
      const sound = this.theSound;
      this.close();
      addToSession(sound);
    },
    goToLogin() {
      this.$router.push({ name: "Login", query: { redirect: this.paneRoute.fullPath } });
    },
    // "It's yours … in My audio, Purchased": open that tab.
    openMyAudio() {
      this.close();
      this.$router.push(MY_PURCHASES);
    },
    async purchase() {
      // One checkout at a time: Enter in the price field must not start a
      // second order while the first is in flight or the page is leaving.
      if (this.loading) return;
      if (!this.signedIn) {
        this.goToLogin();
        return;
      }
      if (this.pricing) return; // the live price is still on its way
      if (!this.isValidAmount) {
        this.amountError = `The minimum is ${usd(this.floor)}.`;
        return;
      }

      this.loading = true;
      this.amountError = "";
      let leaving = false;

      try {
        const response = this.packTarget
          ? await this.purchasePack(this.packTarget.slug, this.amountValue).catch((body) => {
              // The mixin rejects with the server's envelope.
              throw {
                status: body && body.status,
                error: body?.data?.results?.error || null,
                message: (body && body.message) || "",
                min: body?.data?.results?.min_usd ?? null,
              };
            })
          : await this.startPurchase(this.loopHash, this.amountValue, "paypal");
        const results = response?.data?.results;
        const checkoutUrl = results?.checkout_url;

        if (checkoutUrl) {
          // Hand off to PayPal for approval; we capture on return. The sheet
          // stays busy until the page unloads.
          leaving = true;
          this.stopPreview();
          // A pack's cancel page finds its pack from the order itself.
          if (!this.packTarget) rememberCheckoutFrom(this.paneRoute.fullPath);
          window.location.href = checkoutUrl;
        } else if (results?.claimed) {
          this.claimed = true;
          this.$emit("licensed", this.packTarget ? this.packTarget.slug : this.loopHash);
        } else {
          this.amountError = "Couldn't start checkout. Try again.";
        }
      } catch (error) {
        console.error("Purchase error:", error);
        if (error && error.status === 401) {
          this.goToLogin();
          return;
        }
        if (error && error.error === "not_for_sale") {
          // The live terms changed (or never loaded): show the sheet's
          // Not for sale state.
          this.meta = {
            ...(this.meta || {}),
            hash: this.loopHash,
            license_type: "rights-reserved",
            min_price_usd: 0,
            suggested_price_usd: null,
            for_sale: false,
          };
        }
        this.amountError = purchaseErrorText(error, this.floor, { pack: !!this.packTarget });
      } finally {
        if (!leaving) this.loading = false;
      }
    },
  },
};
</script>

<!-- The overlay teleports to <body>, outside this component's scope. -->
<style>
.cl-buy.v-dialog > .v-overlay__content {
  width: 420px;
  max-width: calc(100% - 48px);
}

.cl-buy > .v-overlay__scrim {
  background: var(--cl-scrim) !important;
  opacity: 1 !important;
}

@media (max-width: 699.98px) {
  .cl-buy.v-dialog {
    align-items: flex-end;
  }
  .cl-buy.v-dialog > .v-overlay__content {
    align-self: flex-end;
    width: 100%;
    max-width: 100%;
    max-height: calc(100% - 24px);
    margin: 0;
  }
}
</style>

<style scoped>
.buy-sheet {
  display: flex;
  flex-direction: column;
  min-height: 0;
  max-height: 100%;
  overflow: hidden;
  background: var(--cl-sheet);
  color: var(--cl-label);
  border-radius: var(--cl-r-sheet);
  box-shadow: 0 20px 60px var(--cl-scrim);
  font-family: var(--cl-font);
}

.buy-handle {
  display: none;
  flex-shrink: 0;
  width: 36px;
  height: 5px;
  border-radius: 3px;
  background: var(--cl-label-4);
  margin: 6px auto 0;
}

.buy-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 16px 16px 0;
}

/* Title, creator line, close */
.buy-head {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.buy-head-text {
  flex: 1 1 auto;
  min-width: 0;
}

.buy-title {
  margin: 0;
  font: var(--cl-title2);
  color: var(--cl-label);
  overflow-wrap: anywhere;
}

.buy-sub {
  margin-top: 3px;
  display: flex;
  align-items: center;
  gap: 6px;
  font: var(--cl-footnote);
  /* The row is the 16 px dot (LoopLicense), not the footnote's line box. */
  line-height: 16px;
  color: var(--cl-label-2);
  min-width: 0;
}

.buy-dot {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  flex-shrink: 0;
}

.buy-sub-text {
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* The pack's bonus note: 8 under the subtitle, footnote, label3
   (buy_sheet.dart). */
.buy-head-note {
  margin: 8px 0 0;
  font: var(--cl-footnote);
  color: var(--cl-label-3);
}

.buy-close {
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  margin-top: 4px;
  border: none;
  border-radius: 50%;
  background: var(--cl-control-fill);
  color: var(--cl-label-70);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.buy-close:hover {
  background: var(--cl-control-fill-strong);
}

/* Preview: play, and the true waveform in the creator's colour */
.buy-preview {
  margin-top: 16px;
  border-radius: var(--cl-r-2xl);
  background: var(--cl-cell-inner);
  padding: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.buy-play {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: var(--cl-accent);
  color: var(--cl-on-fill);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
}

.buy-play:hover {
  background: var(--cl-accent-pressed);
}

/* LoopLicense: a 270 x 44 strip from the play button's right, 2 px bars
   on a 3 px pitch, the owner colour at .7. It narrows only when the card
   can't hold it. */
.buy-wave {
  flex: 0 1 270px;
  width: 270px;
  min-width: 0;
  --wave-bar: 2px;
  --wave-pitch: 3px;
}

.buy-wave :deep(.wave-base),
.buy-wave :deep(.wave-loaded) {
  background: var(--buy-owner);
  opacity: 0.7;
}

.buy-wave :deep(.wave-played) {
  background: var(--buy-owner);
}

.buy-note {
  margin: 6px 0 0;
  padding: 0 4px;
  font: var(--cl-caption);
  line-height: 1.2;
  color: var(--cl-label-45);
}

/* License card */
.buy-license {
  margin-top: 18px;
  border-radius: var(--cl-r-2xl);
  background: var(--cl-cell-inner);
  overflow: hidden;
}

.buy-license-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-bottom: 1px solid var(--cl-separator);
  font: var(--cl-body);
  color: var(--cl-label);
}

.buy-license-name {
  color: var(--cl-label-2);
}

.buy-term {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 14px;
  border-bottom: 1px solid var(--cl-separator);
  font: var(--cl-subhead);
  line-height: 1.35;
  color: var(--cl-label-85);
}

.buy-term svg {
  flex-shrink: 0;
  margin-top: 1px;
}

.buy-term--link {
  border-bottom: none;
  text-decoration: none;
  color: var(--cl-label-85);
}

.buy-term--link:hover {
  background: var(--cl-hover);
  color: var(--cl-label);
}

.buy-facts {
  margin-top: 14px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
  padding: 0 4px;
  font: var(--cl-footnote);
  color: var(--cl-label-55);
  font-variant-numeric: tabular-nums;
}

/* Or get the whole pack */
.buy-pack {
  margin-top: 14px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: var(--cl-r-2xl);
  background: var(--cl-cell-inner);
  color: var(--cl-label);
  text-decoration: none;
}

.buy-pack:hover {
  background: linear-gradient(var(--cl-hover), var(--cl-hover)), var(--cl-cell-inner);
  color: var(--cl-label);
}

.buy-pack-text {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.buy-pack-title {
  font: var(--cl-subhead);
  font-weight: 600;
  color: var(--cl-label);
}

.buy-pack-line {
  font: var(--cl-footnote);
  color: var(--cl-label-2);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.buy-pack svg {
  flex-shrink: 0;
}

/* Touch: the board's line boxes. The app pins the title to 26 and the
   facts to 16; the License header row is 44. */
@media (pointer: coarse) {
  .buy-title {
    line-height: 26px;
  }
  .buy-license-head {
    line-height: 20px;
  }
  .buy-facts {
    line-height: 16px;
  }
  .buy-pack-title {
    line-height: 18px;
  }
  .buy-pack-line {
    line-height: 16px;
  }
}

/* Name your price, then the one call to action */
.buy-foot {
  flex-shrink: 0;
  padding: 16px 16px 20px;
}

.buy-price {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 44px;
  padding: 0 14px;
  border-radius: var(--cl-r-2xl);
  background: var(--cl-cell-inner);
  box-shadow: inset 0 0 0 1px transparent;
}

.buy-price:focus-within {
  box-shadow: inset 0 0 0 1px var(--cl-accent-chip-border);
}

.buy-price--error,
.buy-price--error:focus-within {
  box-shadow: inset 0 0 0 1px var(--cl-destructive);
}

.buy-price-label {
  flex: 1 1 auto;
  font: var(--cl-body);
  color: var(--cl-label);
}

.buy-price-input {
  flex: 0 1 auto;
  min-width: 0;
}

.buy-price-input :deep(.v-field) {
  font: var(--cl-body);
  color: var(--cl-label);
  --v-field-padding-top: 0px;
  --v-field-padding-bottom: 0px;
  --v-input-control-height: 40px;
}

.buy-price-input :deep(.v-field__input) {
  min-height: 40px;
  padding-top: 0;
  padding-bottom: 0;
  /* Hug the number so "$" sits against it; browsers without
     field-sizing clamp to 10ch. */
  width: auto;
  field-sizing: content;
  min-width: 3ch;
  max-width: 10ch;
  text-align: right;
  font-variant-numeric: tabular-nums;
  -moz-appearance: textfield;
}

.buy-price-input :deep(.v-field__input::-webkit-outer-spin-button),
.buy-price-input :deep(.v-field__input::-webkit-inner-spin-button) {
  -webkit-appearance: none;
  margin: 0;
}

.buy-price-input :deep(.v-text-field__prefix) {
  min-height: 40px;
  padding-top: 0;
  padding-inline-end: 1px;
  color: var(--cl-label-2);
}

.buy-price-note {
  margin: 6px 0 0;
  padding: 0 4px;
  font: var(--cl-footnote);
  color: var(--cl-label-55);
  font-variant-numeric: tabular-nums;
}

.buy-price-note--error {
  color: var(--cl-destructive-text);
}

.buy-cta {
  margin-top: 14px;
  width: 100%;
}

/* The label, then the arrow 8 after it (LoopLicense: gap 8). */
.buy-cta :deep(.v-btn__content) {
  gap: 8px;
}

.buy-cta--off {
  background: var(--cl-control-fill) !important;
  color: var(--cl-label-3) !important;
  opacity: 1 !important;
}

.buy-foot-note {
  margin: 10px 0 0;
  text-align: center;
  text-wrap: pretty;
  font: var(--cl-footnote);
  line-height: 1.4;
  color: var(--cl-label-50);
}

/* Details: Star (34) and Share (36) beside Close, the call to action with
   Add to session beside it. */
.buy-head-actions {
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  margin-right: -8px;
}
.buy-icon {
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--cl-label-3);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.buy-icon:hover {
  background: var(--cl-hover);
}
.buy-star.is-on {
  color: var(--cl-accent-text);
}
.buy-share {
  width: 36px;
  color: var(--cl-accent-text);
}
.buy-cta-row {
  display: flex;
  gap: 10px;
}
.buy-cta-row .buy-cta {
  flex: 1 1 auto;
  width: auto;
  margin-top: 0;
}
.buy-add {
  flex: 0 0 auto;
  height: var(--cl-hero-h) !important;
  border-radius: var(--cl-r-2xl) !important;
  font-size: 17px !important;
}

.buy-done {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font: var(--cl-subhead);
  color: var(--cl-label-85);
}

.buy-done svg {
  flex-shrink: 0;
}

/* Phones: a bottom sheet with a grabber; the call to action sits 40 px
   above the bottom, as on the board. */
@media (max-width: 699.98px) {
  .buy-sheet {
    border-radius: var(--cl-r-sheet) var(--cl-r-sheet) 0 0;
    min-height: min(704px, calc(100dvh - 24px));
  }
  /* A pack has no preview or facts: the sheet takes its own height. */
  .buy-sheet--pack {
    min-height: 0;
  }
  .buy-handle {
    display: block;
  }
  .buy-body {
    padding-top: 14px;
  }
  .buy-foot {
    padding-bottom: 40px;
  }
}
</style>
